import gql from "graphql-tag";
import * as React from "react";

import ErrorLoading from "@khan/component-screens/error-loading";
import type {AsyncApolloFastlyQueryResult} from "@khan/data-gql-apollo";
import {gqlOp, type GqlOperation} from "@khan/data-gql-core";
import Log from "@khan/logging";

import NotFound from "#app-shell-package/errors/not-found.tsx";
import RouteLoadSpinner from "#app-shell-package/route-load-spinner.tsx";
import ApolloFastlyQuery from "#data-access/gql/apollo-fastly-query.tsx";
import withLocation, {
    type WithLocationProps,
    type WithoutLocation,
} from "#data-access/with-location.tsx";

type Props = WithLocationProps & {
    /**
     * The content to render when we have video data.
     *
     * The type of this is almost the same as VideoContentModel, but not quite
     * as that data comes from the content... queries, rather than the
     * video and videoByYoutubeId queries. This means that some properties have
     * slightly different types.
     *
     * Generating types from GraphQL would help here.
     */
    children: (video: any) => React.ReactNode;
};

type ReadableIdVariables = {
    id: string;
};

export const VIDEO_BY_READABLE_ID_QUERY: GqlOperation<
    any,
    ReadableIdVariables
> = gqlOp(gql`
    query videoByReadableID($id: String!) {
        video(id: $id) {
            id
            assessmentItemTags
            contentKind
            defaultUrlPath
            kind
            nodeSlug
            slug
            sponsored
            title
            translatedDescription
            translatedTitle
            translatedCustomTitleTag
            authorNames
            authorList {
                name
            }
            clarificationsEnabled
            creationDate
            dateAdded
            description
            descriptionHtml
            downloadUrls
            duration
            imageUrl
            kaUrl
            kaUserLicense
            keywords
            readableId
            relativeUrl
            sha
            thumbnailUrls {
                category
                url
            }
            translatedDescriptionHtml
            translatedYoutubeId
            translatedYoutubeLang
            youtubeId
            augmentedTranscript
        }
    }
`);

type YoutubeIdVariables = {
    youtubeId: string;
};

type YoutubIdData = any;

export const VIDEO_BY_YOUTUBE_ID_QUERY: GqlOperation<
    YoutubIdData,
    YoutubeIdVariables
> = gqlOp(gql`
    query videoByYoutubeId($youtubeId: String!) {
        video: videoByYoutubeId(youtubeId: $youtubeId) {
            id
            assessmentItemTags
            contentKind
            defaultUrlPath
            kind
            nodeSlug
            slug
            sponsored
            title
            translatedDescription
            translatedTitle
            translatedCustomTitleTag
            authorNames
            authorList {
                name
            }
            clarificationsEnabled
            creationDate
            dateAdded
            description
            descriptionHtml
            downloadUrls
            duration
            imageUrl
            kaUrl
            kaUserLicense
            keywords
            readableId
            relativeUrl
            sha
            thumbnailUrls {
                category
                url
            }
            translatedDescriptionHtml
            translatedYoutubeId
            translatedYoutubeLang
            youtubeId
            augmentedTranscript
        }
    }
`);

const renderQueryResult = (
    idLoaded: string | null | undefined,
    {error, data, loading}: AsyncApolloFastlyQueryResult<YoutubIdData>,
    children: (video: any) => React.ReactNode,
): React.ReactNode => {
    if (loading) {
        return <RouteLoadSpinner />;
    }

    if (error) {
        Log.log("Unable to load embedded video", {
            idLoaded,
            error: error.toString(),
        });
        return <ErrorLoading />;
    }

    if (data?.video == null) {
        return <NotFound resetSettings={false} />;
    }

    return children(data.video);
};

/**
 * Queries for a video based off query params.
 *
 * If param `slug` is present, then that is used to query by readableId.
 * If not, then if param `v` is present, that is used to query by youtubeId.
 *
 * Absence of both will query for hexaflexagons.
 */
const QueryEmbeddedVideo = (props: Props): React.ReactElement => {
    const {
        children,
        location: {
            queryParams: {slug: readableId, v: youtubeId},
        },
    } = props;

    if (readableId) {
        return (
            <ApolloFastlyQuery
                query={VIDEO_BY_READABLE_ID_QUERY}
                variables={{id: readableId}}
            >
                {(result) => renderQueryResult(readableId, result, children)}
            </ApolloFastlyQuery>
        );
    }

    if (youtubeId) {
        // NOTE(somewhatabstract): Lookup by youtube id is deprecated.
        // This is here as a legacy fallback for older embeds.
        return (
            <ApolloFastlyQuery
                query={VIDEO_BY_YOUTUBE_ID_QUERY}
                variables={{
                    // The embedded video page could have the youtubeId
                    // repeating in the URL, which causes an issue for the
                    // query, e.g. /embed_video?v=D5ymMYcLtv0&v=D5ymMYcLtv0
                    youtubeId:
                        Array.isArray(youtubeId) && youtubeId.length > 0
                            ? youtubeId[0]
                            : youtubeId,
                }}
            >
                {(result) => renderQueryResult(youtubeId, result, children)}
            </ApolloFastlyQuery>
        );
    }

    // No valid id appears to have been provided, so we fallback to this
    // default.
    return (
        <ApolloFastlyQuery
            query={VIDEO_BY_READABLE_ID_QUERY}
            variables={{id: "hexaflexagons"}}
        >
            {(result) => renderQueryResult("hexaflexagons", result, children)}
        </ApolloFastlyQuery>
    );
};

type ExportProps = WithoutLocation<
    JSX.LibraryManagedAttributes<
        typeof QueryEmbeddedVideo,
        React.ComponentProps<typeof QueryEmbeddedVideo>
    >
>;

export default withLocation(
    QueryEmbeddedVideo,
) as React.ComponentType<ExportProps>;
